import { Actions, ACTION_VARIANT } from "../../types/type";
import { DOM } from "../../utils/domActions";
import { TrackingUtil } from "../../utils/tracking";

interface ButtonOptions extends Partial<Actions> {
    className?: string;
    onClick?: (e: Event) => void;
    customIcon?: string;  // Allow custom icon HTML
    campaignId?: string;  // Campaign ID for tracking
}

class Button {
    private static readonly EXTERNAL_ICON = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.5 0C7.03976 0 6.66667 0.373096 6.66667 0.833333C6.66667 1.29357 7.03976 1.66667 7.5 1.66667H8.82149L5.79963 4.68852C5.4742 5.01396 5.4742 5.5416 5.79963 5.86703C6.12507 6.19247 6.65271 6.19247 6.97814 5.86703L10 2.84518V4.16667C10 4.6269 10.3731 5 10.8333 5C11.2936 5 11.6667 4.6269 11.6667 4.16667V0.833333C11.6667 0.373096 11.2936 0 10.8333 0H7.5Z" fill="currentColor"/>
<path d="M11.1111 7.5C11.1111 7.03976 10.738 6.66667 10.2778 6.66667C9.81754 6.66667 9.44444 7.03976 9.44444 7.5V8.61111C9.44444 9.37817 8.82262 10 8.05556 10H3.05556C2.28849 10 1.66667 9.37817 1.66667 8.61111L1.66667 3.61111C1.66667 2.84405 2.28849 2.22222 3.05556 2.22222L4.16667 2.22222C4.6269 2.22222 5 1.84913 5 1.38889C5 0.928651 4.6269 0.555555 4.16667 0.555555L3.05556 0.555555C1.36802 0.555555 0 1.92357 0 3.61111V8.61111C0 10.2986 1.36802 11.6667 3.05556 11.6667H8.05556C9.74309 11.6667 11.1111 10.2986 11.1111 8.61111V7.5Z" fill="currentColor"/>
</svg>`;

    static create(options: ButtonOptions): HTMLElement {
        const {
            label = "",
            url = "#",
            external = false,
            variant = ACTION_VARIANT.PLAIN,
            icon = false,
            className = "",
            onClick,
            customIcon,
            campaignId
        } = options;

        // Determine if it should be a button or link based on variant
        const isButton = variant !== ACTION_VARIANT.PLAIN;
        
        // Build class name based on variant
        let elementClassName = `trustshop-embed--button ${variant}`;
        
        // Add custom className if provided
        if (className) {
            elementClassName += ` ${className}`;
        }

        // Create the element (button or anchor)
        const element = DOM.createElement(isButton ? "button" : "a", {
            className: elementClassName,
            ...(isButton ? {} : {
                attributes: {
                    href: url,
                    target: external ? "_blank" : "_top",
                }
            })
        });

        // Add icon if needed
        if (customIcon) {
            const iconSpan = DOM.createElement("span", {
                innerHTML: customIcon,
                className: "action-icon"
            });
            element.appendChild(iconSpan);
        } else if (icon) {
            // TODO: Handle custom icon when icon is a string/path
        } else if (external && !customIcon) {
            const iconSpan = DOM.createElement("span", {
                innerHTML: this.EXTERNAL_ICON,
                className: "action-icon"
            });
            element.appendChild(iconSpan);
        }

        // Add label only if provided
        if (label) {
            const labelSpan = DOM.createElement("span", {
                textContent: label,
            });
            element.appendChild(labelSpan);
        }

        // Add click handler
        if (onClick) {
            element.addEventListener("click", (e: Event) => {
                if (campaignId) {
                    // Determine action type based on variant
                    const actionType = variant === ACTION_VARIANT.PRIMARY ? 'primary' : 'secondary';
                    TrackingUtil.trackClick(campaignId, actionType);
                }
                onClick(e);
            });
        } else if (isButton && url) {
            // Default click handler for buttons
            element.addEventListener("click", (e: Event) => {
                e.preventDefault();
                if (campaignId) {
                    // Determine action type based on variant
                    const actionType = variant === ACTION_VARIANT.PRIMARY ? 'primary' : 'secondary';
                    TrackingUtil.trackClick(campaignId, actionType);
                }
                
                // Handle SPA navigation vs external navigation
                if (external) {
                    window.open(url, "_blank");
                } else if (url.startsWith('/') && !url.startsWith('//')) {
                    // Internal path - try global navigate function first, fallback to event
                    if ((window as any).__APP_NAVIGATE__) {
                        (window as any).__APP_NAVIGATE__(url);
                    } else {
                        // Fallback to custom event
                        window.dispatchEvent(
                            new CustomEvent('EMBED_NAVIGATE', {
                                detail: { path: url }
                            })
                        );
                    }
                } else {
                    // External URL - open normally
                    window.open(url, "_self");
                }
            });
        } else if (!isButton && campaignId) {
            // For plain links, track clicks (plain links are always secondary)
            element.addEventListener("click", () => {
                TrackingUtil.trackClick(campaignId, 'secondary');
            });
        }

        return element;
    }

    /**
     * Create a primary button
     */
    static createPrimary(options: Omit<ButtonOptions, 'variant'>): HTMLElement {
        return this.create({ ...options, variant: ACTION_VARIANT.PRIMARY });
    }

    /**
     * Create a secondary button
     */
    static createSecondary(options: Omit<ButtonOptions, 'variant'>): HTMLElement {
        return this.create({ ...options, variant: ACTION_VARIANT.SECONDARY });
    }

    /**
     * Create a plain link button
     */
    static createPlain(options: Omit<ButtonOptions, 'variant'>): HTMLElement {
        return this.create({ ...options, variant: ACTION_VARIANT.PLAIN });
    }

    /**
     * Create a close button with default close icon
     */
    static createClose(options: { onClick: () => void; className?: string }): HTMLElement {
        const closeIcon = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.71967 7.78033C7.01256 8.07322 7.48744 8.07322 7.78033 7.78033C8.07322 7.48744 8.07322 7.01256 7.78033 6.71967L5.06066 4L7.78033 1.28033C8.07322 0.987437 8.07322 0.512563 7.78033 0.21967C7.48744 -0.073223 7.01256 -0.073223 6.71967 0.21967L4 2.93934L1.28033 0.21967C0.987437 -0.0732233 0.512564 -0.0732233 0.21967 0.21967C-0.0732225 0.512563 -0.0732226 0.987437 0.21967 1.28033L2.93934 4L0.21967 6.71967C-0.0732233 7.01256 -0.0732233 7.48744 0.21967 7.78033C0.512563 8.07322 0.987437 8.07322 1.28033 7.78033L4 5.06066L6.71967 7.78033Z" fill="white" fill-opacity="0.6"/>
</svg>`;
        
        // Create close button directly as a button element
        const button = DOM.createElement("button", {
            className: `trustshop-embed--close-btn ${options.className || ''}`.trim(),
        });

        // Add icon
        const iconSpan = DOM.createElement("span", {
            innerHTML: closeIcon,
            className: "close-icon"
        });
        button.appendChild(iconSpan);

        // Add click handler
        button.addEventListener("click", options.onClick);

        return button;
    }
}

export default Button;